import React, { useCallback, useContext } from "react";
import styled from "styled-components";
import { TasksTodosTitle } from "./TasksTodosTitle";
import { TasksEmpty } from "./TasksEmpty";
import { TaskTabsTypes } from "../../../shared/constants/taskTabsTypes";
import trackerHelper from "../../../../../coworker-app/src/helpers/tracker";
import { ModalContext } from "../../../layout/context/ModalContextProvider";
import { ModalType } from "../../../layout/context/modalType";
import { SimpleLoadingBall } from "../../../shared/simple/SimpleLoading/SimpleLoading";
import { TaskType } from "../../../tasks/enums/taskTypes";
import {
  Filter,
  FilterValue,
} from "@coworker/apprestructured/src/shared/filters/types/filter";
import { TaskListItem } from "@coworker/apprestructured/src/activity/hooks/useActivityData";
import { FullTask } from "@coworker/apprestructured/src/activity/types/FullTask";
import { useItemsInfoQueries } from "@coworker/apprestructured/src/shared/hooks/item/useItemsInfo";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { TaskCard as TaskCardV1 } from "@coworker/apprestructured/src/tasks/components/TaskCard/TaskCard";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import TaskCardV2 from "@coworker/apprestructured/src/task-management/components/TaskCard/TaskCard";
import ListableTaskCardWrapper from "@coworker/apprestructured/src/task-management/components/TaskCard/wrappers/NormalTaskWrapper/ListableTaskCardWrapper";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";

const TasksTodosContentContainer = styled("div")`
  background-color: var(--white);
  @media (min-width: 1440px) {
    min-height: 187px;
  }
`;

interface TasksTodosProps {
  title: string;
  tasksCount: number;
  tasksList: TaskListItem[];
  tabType: TaskTabsTypes;
  isLoading: boolean;
  hideViewButton?: boolean;
  filters: Filter[];
  filterValues: FilterValue[];
  setFilterValues: (values: FilterValue[]) => void;
}

export const TasksTodos: React.FC<TasksTodosProps> = ({
  title,
  tasksCount,
  tasksList,
  tabType,
  isLoading,
  hideViewButton,
  filters,
  filterValues,
  setFilterValues,
}) => {
  const { toggleModal } = useContext(ModalContext);

  const goToTaskDetail = (taskId: string, taskType: TaskType) => {
    trackerHelper.trackTaskCardPreview();
    toggleModal({
      type: ModalType.TaskDetail,
      payload: { taskId, taskType },
    });
    const section = tabType === TaskTabsTypes.OPEN ? "open_tasks" : "my_tasks";
    trackerHelper.trackTaskCardPreview(section);
  };

  const [, setSearchParams] = useSearchParamState();

  const handleTaskV2CardClick = useCallback(
    (taskId: string) => {
      setSearchParams((params) => {
        params.set("taskViewer", String(taskId));
      });
    },
    [setSearchParams]
  );

  const itemNos =
    tasksList
      .filter(({ type }) => type === "v1")
      .map(({ task_data: task }) => (task as FullTask).product_article_id)
      .filter(Boolean) ?? [];

  const itemsInfo = useItemsInfoQueries(itemNos)
    .map((result) => result.data)
    .filter(Boolean);

  return (
    <div>
      <TasksTodosTitle
        title={title}
        tasksCount={tasksCount}
        tabType={tabType}
        filters={filters}
        filterValues={filterValues}
        setFilterValues={setFilterValues}
      ></TasksTodosTitle>
      <TasksTodosContentContainer className="tasks-todos">
        {isLoading && (
          <div className="loading-ball-center">
            <SimpleLoadingBall text="" size="medium" />
          </div>
        )}
        {tasksCount === 0 && !isLoading && (
          <TasksEmpty
            hideViewButton={hideViewButton as boolean}
            id="title"
            tasksTabType={tabType}
          ></TasksEmpty>
        )}
        {tasksCount > 0 && !isLoading && (
          <FixaListView id={`TasksTodos_${tabType}`} className="task-list-view">
            {tasksList.map(({ type, task_data: task }) => {
              switch (type) {
                case "v1": {
                  return (
                    <TaskCardV1
                      key={`v1_${task.id}`}
                      task={task}
                      item={
                        itemsInfo?.find(
                          (item) => item?.no === task.product_article_id
                        ) as Item
                      }
                      onShowTaskDetails={() =>
                        goToTaskDetail(task.id, task.task_type)
                      }
                    />
                  );
                }
                case "v2": {
                  return (
                    <ListableTaskCardWrapper
                      key={`v2_${task.id}`}
                      aria-label={task.title}
                      onSelect={() => handleTaskV2CardClick(task.id)}
                    >
                      <TaskCardV2 task={task} />
                    </ListableTaskCardWrapper>
                  );
                }
              }
            })}
          </FixaListView>
        )}
      </TasksTodosContentContainer>
    </div>
  );
};
